/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { Button, Grid, PageGridContainer, Typography2, useTheme } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon } from '@coursera/cds-icons';

import SkillTargetsInformationCard from 'bundles/program-common/components/StaticInformationCards/SkillTargetsInformationCard';
import { PlaceholderShimmer } from 'bundles/skills-common';
import ProgressTracker from 'bundles/skills-common/private/progress-tracker/ProgressTracker';
import ProgressTrackerMyScorePopover from 'bundles/skills-common/private/progress-tracker/ProgressTrackerMyScorePopover';
import SkillsInsightMessage from 'bundles/skills-common/private/progress-tracker/SkillsInsightMessage';

import _t from 'i18n!nls/skillset-learner-page';

import type { SkillPercentile, SkillProficency } from '../SkillSetSummaryCard';

const useStyles = () => {
  const { spacing, breakpoints } = useTheme();
  return {
    content: css`
      margin: ${spacing(24, 0, 0)};
    `,
    skillContainer: css`
      margin: ${spacing(24, 0)};
      display: flex;
      flex-wrap: wrap;
      gap: 22px;
      justify-content: space-between;
    `,
    skill: css`
      max-width: 100%;
      flex: 0 0 40%;
      ${breakpoints.down('sm')} {
        flex-basis: 100%;
      }
    `,
    footer: css`
      display: flex;
      align-items: center;
    `,
    faqButton: css`
      margin-left: auto;
    `,
  };
};

type Props = {
  skills: SkillProficency[];
  skillPercentile: SkillPercentile[];
  occupationName: string;
};

const SkillsInformationSection: React.FC<Props> = ({ skills, skillPercentile, occupationName }) => {
  const styles = useStyles();
  const [isFAQOpen, setFAQOpen] = useState(false);
  return (
    <PageGridContainer>
      <Grid item xs={12} css={styles.content}>
        <Typography2 component="h2" variant="subtitleMedium">
          {_t('Skills you will gain')}
        </Typography2>
        <div css={styles.skillContainer}>
          {skills.map((skill) => (
            <div key={skill.skillId} css={styles.skill}>
              <ProgressTracker
                headerSize="sm"
                skillName={skill.skillName}
                target={skill.targetProficiency}
                score={skill.score}
                forScoring
                scoreTooltip={
                  <ProgressTrackerMyScorePopover
                    occupationName={occupationName}
                    percentile={skillPercentile.find(({ skillId }) => skillId === skill.skillId)?.percentile ?? null}
                    score={skill.score}
                    target={skill.targetProficiency}
                    skillName={skill.skillName}
                  />
                }
                segments={[50, 150, 350]}
              />
              <SkillsInsightMessage score={skill.score} skillId={skill.skillId} />
            </div>
          ))}
        </div>
      </Grid>
      <Grid item xs={12} css={styles.footer}>
        <Button
          data-test="faq-button"
          variant="ghost"
          onClick={() => setFAQOpen(!isFAQOpen)}
          css={styles.faqButton}
          iconPosition="after"
          icon={isFAQOpen ? <ChevronUpIcon size="small" /> : <ChevronDownIcon size="small" />}
          aria-expanded={isFAQOpen}
          aria-controls="proficiency-targets-faq"
        >
          {_t('How to read proficiency targets')}
        </Button>
      </Grid>
      <SkillTargetsInformationCard isOpen={isFAQOpen} />
    </PageGridContainer>
  );
};

const SkillsInformationSectionPlaceholder: React.FC = () => {
  const styles = useStyles();
  return (
    <PageGridContainer>
      <Grid item xs={12} css={styles.content}>
        <Typography2 component="h2" variant="subtitleMedium">
          {_t('Skills you will gain')}
        </Typography2>
        <div css={styles.skillContainer}>
          {[0, 1, 2, 3].map(() => (
            <div css={styles.skill}>
              <PlaceholderShimmer height="58px" />
            </div>
          ))}
        </div>
      </Grid>
      <Grid item xs={12} css={styles.footer}>
        <PlaceholderShimmer height="58px" />
      </Grid>
    </PageGridContainer>
  );
};

export default SkillsInformationSection;
export { SkillsInformationSectionPlaceholder };
